import { Fragment, useEffect, useState } from 'react';
import { Transition } from '@headlessui/react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

export interface ToastProps {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  onClose: (id: string) => void;
}

const icons = {
  success: CheckCircleIcon,
  error: XCircleIcon,
  warning: ExclamationTriangleIcon,
  info: InformationCircleIcon,
};

const styles = {
  success: 'bg-success-50 border-success-200 dark:bg-success-900/20 dark:border-success-800/40',
  error: 'bg-danger-50 border-danger-200 dark:bg-danger-900/20 dark:border-danger-800/40',
  warning: 'bg-warning-50 border-warning-200 dark:bg-warning-900/20 dark:border-warning-800/40',
  info: 'bg-primary-50 border-primary-200 dark:bg-primary-900/20 dark:border-primary-800/40',
};

const iconColors = {
  success: 'text-success-500',
  error: 'text-danger-500',
  warning: 'text-warning-500',
  info: 'text-primary-500',
};

export function Toast({ id, type, title, message, duration = 5000, onClose }: ToastProps) {
  const [show, setShow] = useState(true);
  const Icon = icons[type];

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        setShow(false);
        setTimeout(() => onClose(id), 300);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, id, onClose]);

  return (
    <Transition
      show={show}
      as={Fragment}
      enter="transform ease-out duration-300 transition"
      enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
      enterTo="translate-y-0 opacity-100 sm:translate-x-0"
      leave="transition ease-in duration-100"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div
        className={clsx(
          'w-full max-w-sm rounded-xl border shadow-lg p-4 pointer-events-auto',
          styles[type]
        )}
      >
        <div className="flex items-start gap-3">
          <Icon className={clsx('w-5 h-5 flex-shrink-0 mt-0.5', iconColors[type])} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</p>
            {message && <p className="text-sm text-slate-700 dark:text-slate-300 mt-1">{message}</p>}
          </div>
          <button
            onClick={() => {
              setShow(false);
              setTimeout(() => onClose(id), 300);
            }}
            className="flex-shrink-0 p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-white/50 dark:hover:bg-slate-950/30 transition-colors"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </Transition>
  );
}

// Toast Container
export interface ToastContainerProps {
  toasts: ToastProps[];
  onClose: (id: string) => void;
}

export function ToastContainer({ toasts, onClose }: ToastContainerProps) {
  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-3 pointer-events-none">
      {toasts.map((toast) => (
        <Toast key={toast.id} {...toast} onClose={onClose} />
      ))}
    </div>
  );
}

